import type { Defaults, Game, IngestKey } from '@growth/client';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { useGames } from '../components/Layout.tsx';
import { PageHeader } from '../components/Layout.tsx';
import { Card, Empty, ErrorBox, Secret } from '../components/ui.tsx';
import { ENV_LABEL } from '../filters.ts';
import { fmtDay } from '../format.ts';
import { invalidateCache, useAction, useApi, useSession } from '../session.tsx';

const TIMEZONES = ['Europe/Madrid', 'UTC', 'Europe/London', 'America/Mexico_City', 'America/New_York', 'America/Los_Angeles', 'America/Sao_Paulo', 'Asia/Tokyo'];

function CreateGame({ onCreated }: { onCreated: (g: Game, keys: IngestKey[]) => void }) {
  const defaults = useApi<Defaults>('defaults', (api) => api.defaults());
  const { api } = useSession();
  const action = useAction();
  const [form, setForm] = useState({ id: '', name: '', timezone: 'Europe/Madrid', platforms: ['android'], packs: ['survivor'] });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const r = await action.run(() =>
      api.createGame({ id: form.id.trim(), name: form.name.trim(), reporting_timezone: form.timezone, platforms: form.platforms, event_packs: form.packs }),
    );
    if (r) {
      invalidateCache('games');
      onCreated(r.game, r.keys);
    }
  };
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  return (
    <Card title="Nuevo juego" subtitle="Crea el juego y sus tres claves de ingesta (desarrollo, staging, producción).">
      <form className="stack" onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span>ID (slug, será el game_id)</span>
            <input className="input" value={form.id} pattern="[a-z0-9][a-z0-9-]{1,62}" placeholder="antpocalypse" required onChange={(e) => setForm({ ...form, id: e.target.value.toLowerCase() })} />
          </label>
          <label className="field">
            <span>Nombre</span>
            <input className="input" value={form.name} placeholder="Antpocalypse" required onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="field">
            <span>Zona horaria de informes</span>
            <select className="input" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
              {TIMEZONES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="row">
          <span className="muted small">Plataformas:</span>
          {(defaults.data?.platforms ?? ['android', 'ios']).map((p) => (
            <label key={p} className="check">
              <input type="checkbox" checked={form.platforms.includes(p)} onChange={() => setForm({ ...form, platforms: toggle(form.platforms, p) })} />
              {p}
            </label>
          ))}
        </div>
        <div className="row">
          <span className="muted small">Paquetes de eventos:</span>
          <label className="check">
            <input type="checkbox" checked disabled /> core
          </label>
          {(defaults.data?.packs ?? [])
            .filter((p) => p.name !== 'core')
            .map((p) => (
              <label key={p.name} className="check" title={p.description}>
                <input type="checkbox" checked={form.packs.includes(p.name)} onChange={() => setForm({ ...form, packs: toggle(form.packs, p.name) })} />
                {p.name} ({p.events} eventos)
              </label>
            ))}
        </div>
        {action.error && <ErrorBox error={action.error} />}
        <div>
          <button className="btn primary" type="submit" disabled={action.busy}>
            Crear juego
          </button>
        </div>
      </form>
    </Card>
  );
}

export function GamesPage() {
  const games = useGames();
  const { isAdmin } = useSession();
  const [created, setCreated] = useState<{ game: Game; keys: IngestKey[] } | null>(null);

  return (
    <>
      <PageHeader title="Juegos" sub="Un backend y un panel para todos los juegos de Hive Interactive." />
      {games.error && <ErrorBox error={games.error} onRetry={games.reload} />}
      {created && (
        <Card title={`${created.game.name} creado`} subtitle="Copia estas claves en el asset GameGrowthSettings de Unity (son publicables, no secretas).">
          <div className="stack">
            {created.keys.map((k) => (
              <div key={k.id} className="row">
                <span className={`badge env-${k.environment}`}>{ENV_LABEL[k.environment]}</span>
                <Secret value={k.key} />
              </div>
            ))}
            <Link to={`/g/${created.game.id}/settings/sdk`}>Ver guía de integración del SDK →</Link>
          </div>
        </Card>
      )}
      <div className="grid cols-3" style={{ marginTop: 16 }}>
        {(games.data ?? []).map((g) => (
          <Card
            key={g.id}
            title={<Link to={`/g/${g.id}`}>{g.name}</Link>}
            subtitle={
              <>
                <code>{g.id}</code> · {g.platforms.join(', ')} · {g.reporting_timezone}
              </>
            }
          >
            <dl className="kv">
              <dt>Paquetes</dt>
              <dd>{g.event_packs.join(', ')}</dd>
              <dt>Último día con datos (prod)</dt>
              <dd>{g.last_production_day ? fmtDay(g.last_production_day) : 'sin datos aún'}</dd>
              <dt>Último día con datos (dev)</dt>
              <dd>{g.last_development_day ? fmtDay(g.last_development_day) : 'sin datos aún'}</dd>
              {g.archived_at && (
                <>
                  <dt>Estado</dt>
                  <dd>archivado</dd>
                </>
              )}
            </dl>
          </Card>
        ))}
      </div>
      {games.data && games.data.length === 0 && <Empty>Aún no hay juegos. Crea el primero para obtener sus claves de ingesta.</Empty>}
      {isAdmin && <CreateGame onCreated={(game, keys) => setCreated({ game, keys })} />}
    </>
  );
}
